/**
 * A Google Photos pick's shape and its shared parts: what a pick hands back
 * (GooglePhotoPick), how far it has got (PickProgress), where photos go with
 * no dialog open (PickFallback), the pick a tap started in this tab
 * (LivePick) and the pending pick this tab is bringing in (Runner), this
 * tab's id for its hold on a pending pick, and how a failed pick is sent to
 * Sentry (reportFailure, with its reason and the step it stopped at).
 *
 * Moved word for word out of GooglePhotosService (W1.18 part 3, 2026-10-26),
 * which passes the public types on to the photo dialogs and
 * GooglePhotosResume; the sign-in's wait is in googlePhotosSignIn, the
 * window and the download in googlePhotosPicker, the steps that need no
 * window in googlePhotosFlow.
 *
 * Requests: none of its own. reportFailure sends to Sentry through
 * reportOtherFailure (shared/lib/serverFailure); the token is never part of
 * what is sent. Browser storage: localStore() hands the others
 * localStorage, where the Google Photos sign-in keys and the pending pick
 * live (googlePhotosFlow names them), or null where the browser blocks it.
 *
 * @module googlePhotosSession
 */

import { debugLog } from '@/config/config';
import { reportOtherFailure } from '@/shared/lib/serverFailure';
import { GooglePhotosError, newState, type GooglePhotosFailure, type PickTarget } from './googlePhotosFlow';

/** A picture from Google, and the day Google says it was taken. */
export interface GooglePhotoPick {
  /** The picture's bytes, named as Google names it. */
  file: File;
  /** Null when Google did not say. */
  takenAt: Date | null;
  /** Google's id for the picked item, sent with the upload so the server can skip the same item picked twice (B61). */
  googleItemId?: string;
}

/**
 * Where a pick has got to, for the words under the Google Photos row:
 * 'opening' while the session is made, 'waiting' while the gardener chooses
 * (and signs in, the first time), 'fetching' while the photos come over.
 */
export type GooglePhotosStage = 'opening' | 'waiting' | 'fetching';

/** Told to the dialog as a pick moves on. */
export interface PickProgress {
  /** Where the pick has got to. */
  stage: GooglePhotosStage;
  /** How many photos are coming over, while fetching. */
  count: number;
}

/**
 * Where photos go when the dialog that asked for them is no longer open in
 * this tab (closed, or the tab was reloaded), and how the gardener is told.
 * Set by GooglePhotosResume.
 */
export interface PickFallback {
  /**
   * The photos are on their way, with no dialog to say so.
   *
   * @param count - How many.
   * @param target - Where they are going.
   */
  adding: (count: number, target: PickTarget) => void;
  /**
   * Takes the photos in (the Photos tray).
   *
   * @param picks - The photos.
   * @param target - Where they were going.
   */
  deliver: (picks: GooglePhotoPick[], target: PickTarget) => Promise<void>;
  /**
   * A pick with no dialog to say so failed.
   *
   * @param error - Why; already sent to Sentry.
   */
  failed: (error: unknown) => void;
}

/** The step a pick stopped at, as Sentry is told. */
export type PickStep = 'open' | 'sign-in' | 'session' | 'resume' | 'poll' | 'list' | 'download' | 'deliver';

/** A pick started by a tap in this tab, and the dialog waiting on it. */
export interface LivePick {
  /** The pick's id: the sign-in's state, or a fresh one. */
  pickId: string;
  /** Where the photos are going. */
  target: PickTarget;
  /** Whether several photos may be picked. */
  many: boolean;
  /** Stops the steps before the session is handed over. */
  controller: AbortController;
  /** The window the tap opened, closed when the pick is over. */
  win: Window | null;
  /** What the dialog awaits. */
  promise: Promise<GooglePhotoPick[]>;
  /** Ends the promise with the photos. */
  resolve: (picks: GooglePhotoPick[]) => void;
  /** Ends the promise with a failure. */
  reject: (error: unknown) => void;
  /** Told as the pick moves on. */
  onProgress: (progress: PickProgress) => void;
  /** True once the dialog has closed: the photos go to the fallback. */
  detached: boolean;
  /** True once the promise has ended. */
  settled: boolean;
}

/** The pending pick this tab is bringing in. */
export interface Runner {
  /** The pick's id. */
  pickId: string;
  /** Stops it. */
  controller: AbortController;
  /** True once the photos are being fetched: a new tap no longer stops it. */
  committed: boolean;
}

/** A pick stopped quietly: another tab holds it, or it was brought in or cancelled. */
export class Stopped extends Error {}

/** Failures the gardener is not told about by toast when no dialog is open: they chose them, or know. */
export const QUIET_FALLBACK: ReadonlySet<GooglePhotosFailure> = new Set<GooglePhotosFailure>([
  'cancelled', 'timed-out', 'consent-denied', 'scope-denied', 'popup-blocked', 'not-configured',
]);

/** This tab's id, for its hold on a pending pick. */
export const TAB_ID = newState();

/** fetch, bound so it can be handed around without losing `window`. */
export const boundFetch = (input: string, init?: RequestInit): Promise<Response> => fetch(input, init);

/**
 * localStorage, or null where the browser blocks it.
 *
 * @returns The store, or null.
 */
export function localStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * The reason a failure carries.
 *
 * @param error - Whatever was thrown.
 * @returns Its reason; 'unknown' for anything not ours.
 */
export function reasonOf(error: unknown): GooglePhotosFailure {
  return error instanceof GooglePhotosError ? error.reason : 'unknown';
}

/**
 * Sends a failed pick to Sentry, with its reason and the step it stopped at,
 * so the next failure says where. A cancel is not sent. The token is never
 * part of what is sent: the messages are ours, or a fetch's own.
 *
 * @param error - Whatever was thrown.
 * @param step - Where it stopped.
 */
export function reportFailure(error: unknown, step: PickStep): void {
  const reason = reasonOf(error);
  if (reason === 'cancelled') return;
  const detail = error instanceof Error ? error.message : String(error);
  debugLog('useGooglePhotos', `Google Photos pick failed at ${step}`, error);
  // Reported under a reference code, so the gardener's "told about it" message can quote it (E3).
  reportOtherFailure(error, 'google-photos', {
    tags: { failure: 'google-photos', googlePhotosReason: reason, googlePhotosStep: step },
    extras: {
      detail,
      online: typeof navigator === 'undefined' ? null : navigator.onLine,
      visibility: typeof document === 'undefined' ? null : document.visibilityState,
    },
    fingerprint: ['google-photos', reason, step],
  }, `Google Photos pick failed: ${reason} at ${step}`);
}
